import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getKeycloak, withVerifiedToken } from '../auth/keycloak';
import { createGraphQLClient } from '../lib/graphql-client';
import { FILTER_OPTIONS_QUERY } from '../lib/queries';
import type { VehicleBrandOption } from '../types/vehicle';

export const FILTER_OPTIONS_QUERY_KEY = 'vehicle-filter-options';

/** The index is rebuilt nightly, so options fetched once outlive any search a user will run. */
const STALE_TIME_MS = 5 * 60 * 1000;

interface FilterOptionsGraphQLResponse {
  managementVehicleFilterOptions: VehicleBrandOption[];
}

/**
 * Brand and model values for the search dropdowns.
 *
 * One request covers both: the backend nests each brand's models, so choosing a brand narrows the
 * model list without another round trip. Measured at 59 brands and 578 models, which is a few KB.
 */
export function useVehicleFilterOptions(): UseQueryResult<VehicleBrandOption[], Error> {
  return useQuery({
    queryKey: [FILTER_OPTIONS_QUERY_KEY],
    staleTime: STALE_TIME_MS,
    queryFn: async () => {
      return withVerifiedToken(getKeycloak(), async (verifiedToken) => {
        const data =
          await createGraphQLClient(verifiedToken).request<FilterOptionsGraphQLResponse>(FILTER_OPTIONS_QUERY);

        return data.managementVehicleFilterOptions;
      });
    }
  });
}
